/**
 * Generate-flow state machine (spec §3 procedural-first seed-rolled
 * generation): POST /api/models/{id}/generate → follow the job's SSE stream
 * (/api/jobs/{id}/events) with stage labels → terminal state.
 *
 * The reducer is pure (deterministic transition table) so the whole flow is
 * unit-testable; `runGenerateFlow` wires it to fetch + EventSource, both
 * injectable for tests. Resource loading on completion is the store's job.
 */
import { generateMepSystem } from "../api/client";
import type { GenerateRequest } from "../api/types";

/**
 * Engine pipeline stages → progress labels shown in the panel: fire-alarm
 * stages (spec §6.2) plus the HVAC pipeline (spec §2 item 5b: airflow →
 * terminals → routing → sizing → fittings → validation). Unknown stages fall
 * back to a humanized form of the stage key, so server-side stage renames
 * degrade gracefully.
 */
export const STAGE_LABELS: Record<string, string> = {
  placement: "Placing devices…",
  notification: "Checking notification coverage…",
  initiating: "Placing initiating devices…",
  zoning: "Partitioning zones…",
  routing: "Routing circuits…",
  validation: "Validating against rules…",
  // HVAC pipeline stages.
  airflow: "Computing airflow requirements…",
  terminals: "Placing diffusers & grilles…",
  duct_routing: "Routing ductwork…",
  sizing: "Sizing ducts…",
  fittings: "Generating fittings…",
  // Equipment stages of the same HVAC scope (Mechanical is HVAC expanded).
  load_screening: "Screening loads…",
  place_equipment: "Placing equipment…",
  clearance_check: "Checking equipment clearances…",
};

export function stageLabel(stage: string | null): string {
  if (stage === null) return "Generating…";
  return STAGE_LABELS[stage] ?? `${stage.replace(/_/g, " ")}…`;
}

export type GenerateState =
  | { phase: "idle" }
  | { phase: "submitting" }
  | {
      phase: "running";
      jobId: string;
      mepSystemId: string;
      seed: number;
      progress: number;
      stage: string | null;
    }
  | { phase: "done"; mepSystemId: string; seed: number }
  | { phase: "failed"; message: string };

export type GenerateEvent =
  | { type: "submit" }
  | { type: "accepted"; jobId: string; mepSystemId: string; seed: number }
  | { type: "progress"; progress: number; stage: string | null }
  | { type: "complete" }
  | { type: "error"; message: string }
  | { type: "reset" };

export const IDLE: GenerateState = { phase: "idle" };

/** Pure transition function. Unexpected events leave the state unchanged. */
export function reduceGenerate(state: GenerateState, event: GenerateEvent): GenerateState {
  switch (event.type) {
    case "submit":
      // Restartable from any settled state; a running flow ignores re-submit.
      return state.phase === "submitting" || state.phase === "running"
        ? state
        : { phase: "submitting" };
    case "accepted":
      return state.phase === "submitting"
        ? {
            phase: "running",
            jobId: event.jobId,
            mepSystemId: event.mepSystemId,
            seed: event.seed,
            progress: 0,
            stage: null,
          }
        : state;
    case "progress":
      return state.phase === "running"
        ? {
            ...state,
            progress: Math.min(100, Math.max(0, event.progress)),
            stage: event.stage ?? state.stage,
          }
        : state;
    case "complete":
      return state.phase === "running"
        ? { phase: "done", mepSystemId: state.mepSystemId, seed: state.seed }
        : state;
    case "error":
      return state.phase === "idle" ? state : { phase: "failed", message: event.message };
    case "reset":
      return IDLE;
  }
}

/** Minimal EventSource surface so tests can inject a fake. */
export interface EventSourceLike {
  addEventListener(type: string, listener: (ev: MessageEvent<string>) => void): void;
  close(): void;
  onerror: ((ev: Event) => void) | null;
}

export type EventSourceFactory = (url: string) => EventSourceLike;

export interface RunGenerateOptions {
  modelId: string;
  request: GenerateRequest;
  /** Called after every transition with the new state. */
  onState: (state: GenerateState) => void;
  /** Injectable for tests; defaults to the API client (global fetch). */
  generate?: typeof generateMepSystem;
  /** Injectable for tests; defaults to the browser EventSource. */
  eventSource?: EventSourceFactory;
}

export function parsePayload(data: string): Record<string, unknown> {
  try {
    const parsed: unknown = JSON.parse(data);
    return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

/**
 * Run one generation: POST, then follow the job's SSE stream until a terminal
 * event. Resolves with the terminal state (`done` or `failed`). The returned
 * promise never rejects — failures land in the `failed` state.
 */
export function runGenerateFlow(opts: RunGenerateOptions): Promise<GenerateState> {
  const generate = opts.generate ?? generateMepSystem;
  const makeSource: EventSourceFactory =
    opts.eventSource ?? ((url) => new EventSource(url) as unknown as EventSourceLike);

  let state: GenerateState = IDLE;
  const dispatch = (event: GenerateEvent): GenerateState => {
    state = reduceGenerate(state, event);
    opts.onState(state);
    return state;
  };

  return new Promise((resolve) => {
    dispatch({ type: "submit" });
    generate(opts.modelId, opts.request).then(
      (accepted) => {
        dispatch({
          type: "accepted",
          jobId: accepted.job_id,
          mepSystemId: accepted.mep_system_id,
          seed: accepted.seed,
        });

        const source = makeSource(`/api/jobs/${encodeURIComponent(accepted.job_id)}/events`);
        let settled = false;
        const finish = (event: GenerateEvent) => {
          if (settled) return;
          settled = true;
          source.close();
          resolve(dispatch(event));
        };

        source.addEventListener("progress", (ev) => {
          const payload = parsePayload(ev.data);
          dispatch({
            type: "progress",
            progress: typeof payload.progress === "number" ? payload.progress : 0,
            stage: typeof payload.stage === "string" ? payload.stage : null,
          });
        });
        source.addEventListener("complete", () => finish({ type: "complete" }));
        source.addEventListener("error", (ev) => {
          const payload = parsePayload(ev.data ?? "");
          finish({
            type: "error",
            message: typeof payload.message === "string" ? payload.message : "generation failed",
          });
        });
        source.addEventListener("cancelled", () =>
          finish({ type: "error", message: "generation cancelled" }),
        );
        // Transport-level failure (the stream also closes normally after the
        // terminal event; by then we have already resolved and closed).
        source.onerror = () => {
          if (state.phase === "running") {
            finish({ type: "error", message: "lost connection to the job stream" });
          }
        };
      },
      (err: unknown) => {
        resolve(dispatch({ type: "error", message: err instanceof Error ? err.message : String(err) }));
      },
    );
  });
}
